<script>
import each from 'lodash/each';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import isObject from 'lodash/isObject';
import moment from 'moment-timezone';

export default {
  props: {
    placeholder: String,
    timezone: String,
    processing: Boolean,
    isPill: [Boolean, String],
    isRangePicker: [Boolean, String],
    disableBefore: Object,
    disableAfter: Object,
    floatLabel: [Boolean, String],
    defaultDate: Object,
    format: String,
    enableTime: [Boolean, String], // can only be used when isRangePicker = false
    clearable: [Boolean, String],
  },
  data: () => ({
    days: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    meridiems: ['AM', 'PM'],
    times: [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    months: {
      '1': 'January',
      '2': 'February',
      '3': 'March',
      '4': 'April',
      '5': 'May',
      '6': 'June',
      '7': 'July',
      '8': 'August',
      '9': 'September',
      '10': 'October',
      '11': 'November',
      '12': 'December',
    },
    years: [],
    today: moment(),
    selectedMonth: null,
    selectedYear: null,
    daysInWeek: 7,
    visible: false,
    calendarPosition: {},
    anchorElement: null,

    //used when isRangePicker = false
    selectedDay: null,
    prevSelectedDay: null,

    // used when isRangePicker = true
    selectedDays: [],
    hoverDays: [],
    prevSelectedDays: null,

    // used when enableTime = true
    showTimeSelector: false,
    selectedTime: null,
    pendingDay: null,
  }),
  computed: {
    currentMonth() {
      let month = moment(`${this.selectedMonth}/1/${this.selectedYear}`, 'M/D/YYYY');

      if (this.timezone) {
        month = moment.tz(`${this.selectedMonth}/1/${this.selectedYear}`, 'M/D/YYYY', this.timezone);
      }

      return month;
    },
    daysInMonthByWeek() {
      let daysInMonthByWeek = {},
        daysInMonth = this.currentMonth.daysInMonth();

      for (let i = 1; i <= daysInMonth; i++) {
        let dateClone = this.currentMonth.clone().date(i),
          weekNumber = Math.ceil((dateClone.date() + this.currentMonth.day()) / 7);

        dateClone.date(i);

        if (!daysInMonthByWeek[weekNumber]) {
          daysInMonthByWeek[weekNumber] = [null, null, null, null, null, null, null];
        }

        daysInMonthByWeek[weekNumber][dateClone.day()] = dateClone.date();
      }

      return daysInMonthByWeek;
    },
    value() {
      let format = this.format ? this.format : 'M/D/YY';

      if (this.isRangePicker) {
        if (this.selectedDays.length === 1) {
          return `${this.selectedDays[0].format(format)} -`;
        } else if (this.selectedDays.length === 2) {
          return `${this.selectedDays[0].format(format)} - ${this.selectedDays[1].format(format)}`;
        } else {
          return null;
        }
      } else {
        return this.selectedDay ? this.selectedDay.format(format) : null;
      }
    },
    containerClasses() {
      let classes = [];

      if (!this.isPill) {
        classes.push('w-full');
      }

      if (this.floatLabel) {
        classes.push('floatl floatl--full-width');

        if (this.value) {
          classes.push('floatl--active');
        }
      }

      return classes;
    },
    inputClasses() {
      let classes = [];

      if (this.isPill) {
        classes.push('vs-tab text-center hover_text-white');

        if (this.processing) {
          classes.push('vs-tab--disabled');
        }

        if (this.value) {
          classes.push('w-12 vs-tab--active');
        } else {
          classes.push('w-7 text-text');
        }
      } else {
        classes.push('vs-input w-full cursor-pointer');
      }

      if (this.floatLabel) {
        classes.push('floatl__input')
      }

      return classes;
    },
  },
  watch: {
    defaultDate() {
      this.$_initDefaultDateAndTime();
    },
  },
  created() {
    if (this.timezone) {
      this.today = moment.tz(this.timezone);
    }

    this.selectedMonth = this.today.month() + 1;

    this.$_initYearList();

    this.$_initDefaultDateAndTime();

    if (this.disableBefore) {
      this.disableBefore.endOf('hour');
    }

    if (this.disableAfter) {
      this.disableAfter.endOf('hour');
    }

    window.addEventListener('mouseup', this.$_handleClick);

    window.addEventListener('scroll', this.$_adjustCalendarPosition);
  },
  beforeDestroy() {
    window.removeEventListener('mouseup', this.$_handleClick);

    window.removeEventListener('scroll', this.$_adjustCalendarPosition);
  },
  methods: {
    $_initYearList() {
      let year = this.today.year();

      this.selectedYear = year;

      year += 1;

      for (year; year >= 2014; year--) {
        this.years.push(year);
      }
    },
    $_initDefaultDateAndTime() {
      if (this.defaultDate) {
        this.selectedDay = this.defaultDate;

        this.prevSelectedDay = this.defaultDate;

        this.selectedTime = this.$_militaryToStandardTime(this.defaultDate.hours());
      }
    },
    $_militaryToStandardTime(hours) {
      let timeObj = {
        hours,
        meridiem: 'AM',
      };

      if (timeObj.hours > 12) {
        timeObj.hours = timeObj.hours - 12;

        timeObj.meridiem = 'PM';
      } else if (timeObj.hours === 0) {
        timeObj.hours = 12;
      } else if (timeObj.hours === 12) {
        timeObj.meridiem = 'PM';
      }

      return timeObj;
    },
    $_closeDatePicker() {
      this.visible = false;

      this.showTimeSelector = false;

      if (this.isRangePicker) {
        if (this.prevSelectedDays) {
          this.selectedDays = this.prevSelectedDays;

          this.$_selectMonthAndYear(this.selectedDays[0].month() + 1, this.selectedDays[0].year());
        } else {
          this.selectedDays = [];

          this.$_selectMonthAndYear(this.today.month() + 1, this.today.year());
        }
      } else {
        if (this.prevSelectedDay) {
          this.selectedDay = this.prevSelectedDay;

          this.$_selectMonthAndYear(this.selectedDay.month() + 1, this.selectedDay.year());
        } else {
          this.selectedDay = null;

          this.$_selectMonthAndYear(this.today.month() + 1, this.today.year());
        }

        this.pendingDay = null;
      }
    },
    $_handleClick(e) {
      if (!e.target.closest('.vs-date') && !e.target.closest('.calendar')) {
        this.$_closeDatePicker();
      }
    },
    $_selectMonthAndYear(month, year) {
      this.selectedMonth = month;

      this.selectedYear = year;
    },
    $_adjustCalendarPosition() {
      if (this.anchorElement && this.visible) {
        let boundingBox = this.anchorElement.getBoundingClientRect();

        if (window.outerWidth < boundingBox.left + document.querySelector('.calendar').clientWidth) {
          this.calendarPosition = {
            top: `${boundingBox.bottom + 20}px`,
            left: `${boundingBox.left - document.querySelector('.calendar').clientWidth + this.$refs.dateInput.clientWidth}px`,
          }
        } else {
          this.calendarPosition = {
            top: `${boundingBox.bottom + 20}px`,
            left: `${boundingBox.left}px`,
          }
        }
      }
    },
    async inputFocused(e) {
      this.visible = true;

      await this.$nextTick();

      this.anchorElement = e.target;

      this.$_adjustCalendarPosition();
    },
    selectMonth(e) {
      this.selectedMonth = parseInt(e.target.value);
    },
    selectYear(e) {
      this.selectedYear = parseInt(e.target.value);
    },
    clickPreviousMonth(e) {
      e.preventDefault();

      this.selectedMonth -= 1;

      if (this.selectedMonth < 1) {
        this.selectedMonth = 12;

        this.selectedYear -= 1;
      }

      if (this.selectedYear < 2014) {
        this.selectedYear = 2014;
      }
    },
    clickNextMonth(e) {
      e.preventDefault();

      this.selectedMonth += 1;

      if (this.selectedMonth > 12) {
        this.selectedMonth = 1;

        this.selectedYear += 1;
      }

      if (this.selectedYear > this.today.year() + 1) {
        this.selectedYear = this.today.year() + 1;
      }
    },
    clickDate(day, isDisabled) {
      if (day && !isDisabled) {
        let dateString = `${this.selectedMonth}/${day}/${this.selectedYear}`,
          date = this.timezone ? moment.tz(dateString, 'M/D/YYYY', this.timezone) : moment(dateString, 'M/D/YYYY');

        if (this.isRangePicker) {
          if (!this.selectedDays.length) {
            this.selectedDays.push(date);

            this.hoverDays.push(date);
          } else if (this.selectedDays.length === 1) {
            this.selectedDays = date.isBefore(this.selectedDays[0]) ? [date.startOf('day'), this.selectedDays[0].endOf('day')] : [this.selectedDays[0].startOf('day'), date.endOf('day')];

            this.prevSelectedDays = this.selectedDays;

            this.hoverDays = [];

            this.$emit('date-range-changed', {
              start: this.selectedDays[0],
              end: this.selectedDays[1],
              localStart: cloneDeep(this.selectedDays[0]).local(),
              localEnd: cloneDeep(this.selectedDays[1]).local(),
            });

            this.$_closeDatePicker();
          } else {
            this.selectedDays = [date];

            this.hoverDays = [];
          }
        } else {
          if (this.enableTime) {
            this.showTimeSelector = true;

            this.pendingDay = date;
          } else {
            this.selectedDay = date;

            this.prevSelectedDay = this.selectedDay;

            this.$emit('input', this.selectedDay);

            this.$_closeDatePicker();
          }
        }
      }
    },
    hoverDate(day) {
      if (day) {
        let dateString = `${this.selectedMonth}/${day}/${this.selectedYear}`,
          date = this.timezone ? moment.tz(dateString, 'M/D/YYYY', this.timezone) : moment(dateString, 'M/D/YYYY');

        if (this.isRangePicker) {
          if (!this.selectedDays.length) {
            this.hoverDays = [date];
          } else if (this.selectedDays.length === 1) {
            this.hoverDays = date.isBefore(this.selectedDays[0]) ? [date.startOf('day'), this.selectedDays[0].endOf('day')] : [this.selectedDays[0].startOf('day'), date.endOf('day')];
          } else {
            this.hoverDays = [];
          }
        }
      }
    },
    leaveDate(day) {
      if (this.isRangePicker) {
        if (day && this.hoverDays.length === 2) {
          this.hoverDays = [this.hoverDays[0]];
        }
      }
    },
    isDaySelected(day) {
      if (day) {
        let dateString = `${this.selectedMonth}/${day}/${this.selectedYear}`,
          date = this.timezone ? moment.tz(dateString, 'M/D/YYYY', this.timezone) : moment(dateString, 'M/D/YYYY');

        return this.isRangePicker ? find(this.selectedDays, selectedDay => selectedDay.isSame(date, 'day')) : this.selectedDay && this.selectedDay.isSame(date, 'day');
      } else {
        return false;
      }
    },
    isDayHighlighted(day) {
      if (this.isRangePicker && day && (this.selectedDays.length === 2 || this.hoverDays.length === 2)) {
        let dateString = `${this.selectedMonth}/${day}/${this.selectedYear}`,
          date = this.timezone ? moment.tz(dateString, 'M/D/YYYY', this.timezone) : moment(dateString, 'M/D/YYYY');

        return this.selectedDays.length === 2 ? date.isBetween(this.selectedDays[0], this.selectedDays[1], 'day') : date.isBetween(this.hoverDays[0], this.hoverDays[1], 'day');
      } else {
        return false;
      }
    },
    isFirstDay(day) {
      let dateString = `${this.selectedMonth}/${day}/${this.selectedYear}`,
        date = this.timezone ? moment.tz(dateString, 'M/D/YYYY', this.timezone) : moment(dateString, 'M/D/YYYY');

      return this.selectedDays.length === 2 && isObject(this.selectedDays[0]) && this.selectedDays[0].isSame(date, 'day');
    },
    isLastDay(day) {
      let dateString = `${this.selectedMonth}/${day}/${this.selectedYear}`,
        date = this.timezone ? moment.tz(dateString, 'M/D/YYYY', this.timezone) : moment(dateString, 'M/D/YYYY');

      return this.selectedDays.length === 2 && isObject(this.selectedDays[1]) && this.selectedDays[1].isSame(date, 'day');
    },
    isDayDisabled(day) {
      let dateString = `${this.selectedMonth}/${day}/${this.selectedYear}`,
        date = this.timezone ? moment.tz(dateString, 'M/D/YYYY', this.timezone) : moment(dateString, 'M/D/YYYY'),
        disableBeforeWithoutTime = cloneDeep(this.disableBefore),
        disableAfterWithoutTime = cloneDeep(this.disableAfter);

      if (disableBeforeWithoutTime) {
        disableBeforeWithoutTime.startOf('day');
      }

      if (disableAfterWithoutTime) {
        disableAfterWithoutTime.endOf('day');
      }

      return !day || (this.disableBefore && date.isBefore(disableBeforeWithoutTime)) || (this.disableAfter && date.isAfter(disableAfterWithoutTime));
    },
    isTimeSelected(hours, meridiem) {
      if (hours > 12) {
        hours = hours - 12;
      } else if (hours === 0) {
        hours = 12;
      }

      return this.selectedTime.hours === hours && this.selectedTime.meridiem === meridiem;
    },
    isTimeDisabled(hours, meridiem) {
      if (meridiem === 'PM' && hours !== 12) {
        hours += 12;
      } else if (meridiem === 'AM' && hours === 12) {
        hours = 0;
      }

      if (this.disableBefore && this.pendingDay.format('M/D/YYYY') === this.disableBefore.format('M/D/YYYY')) {
        return hours < this.disableBefore.hours();
      } else if (this.disableAfter && this.pendingDay.format('M/D/YYYY') === this.disableAfter.format('M/D/YYYY')) {
        return hours > this.disableAfter.hours();
      }

      return false;
    },
    clickTime(hours, meridiem, isDisabled) {
      if (!isDisabled) {
        this.selectedTime = {
          hours,
          meridiem,
        };

        if (meridiem === 'PM' && hours !== 12) {
          hours += 12;
        } else if (meridiem === 'AM' && hours === 12) {
          hours = 0;
        }

        this.pendingDay.hours(hours);

        this.selectedDay = this.pendingDay;

        this.prevSelectedDay = this.selectedDay;

        this.pendingDay = null;

        this.$emit('input', this.selectedDay);

        this.$_closeDatePicker();
      }
    },
    clear(emitEvents) {
      if (this.isRangePicker) {
        this.selectedDays = [];

        this.prevSelectedDays = null;

        if (emitEvents) {
          this.$emit('date-range-changed', {});
        }
      } else {
        this.selectedDay = null;

        this.prevSelectedDay = null;

        if (emitEvents) {
          this.$emit('input', null);
        }
      }
    },
    setDate(start, end) {
      if (this.isRangePicker) {
        this.selectedDays = [start, end];

        this.prevSelectedDays = this.selectedDays;

        this.$_selectMonthAndYear(this.selectedDays[0].month() + 1, this.selectedDays[0].year());
      } else {
        this.selectedDay = start;

        this.prevSelectedDay = this.selectedDay;

        this.$_selectMonthAndYear(this.selectedDay.month() + 1, this.selectedDay.year());
      }
    },
  },
};
</script>

<template lang='pug'>
.flex.relative.vs-date(
  :class='containerClasses',
)
  .vs-input__icon.vs-input__prepend.flex.items-center(v-if='isRangePicker && clearable')
    .mdi.mdi-calendar

  input(
    ref='dateInput',
    readonly,
    type='text',
    :placeholder='floatLabel ? null : placeholder',
    :disabled='processing',
    :class='inputClasses',
    :value='value',
    @focus='inputFocused',
  )

  .vs-input__clear.mdi.mdi-close(
    v-show='clearable && value',
    @click='clear(true)',
  )

  template(v-if='floatLabel')
    .floatl__label.select-none {{placeholder}}
    .floatl__bg.top-0.left-0.w-full.h-full.absolute

  portal(to='body')
    .calendar.bg-white.rounded-xl.shadow-2xl.fixed.select-none.px-1.py-_5.w-20.z-1000(
      :class='{ "hidden": !visible }',
      :style='calendarPosition',
    )
      .flex.w-full.items-center
        .w-1-8.rounded-none.text-brand.cursor-pointer(
          :disabled='selectedMonth === 1 && selectedYear === 2014',
          @click='clickPreviousMonth',
        )
          svg(style='width:24px;height:24px' viewBox='0 0 24 24')
            path(fill='currentColor' d='M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z')


        select.p-_5.text-right.text-brand.text-sm.border-none.w-full(
          :value='selectedMonth',
          @input='selectMonth',
        )
          option(
            v-for='(month, key) in months'
            :value='key',
          ) {{month}}
        select.p-_5.text-brand.text-sm.border-none.w-full(
          :value='selectedYear',
          @input='selectYear',
        )
          option(
            v-for='year in years'
            :value='year',
          ) {{year}}

        .w-1-8.rounded-none.text-brand.cursor-pointer(
          :disabled='selectedMonth === 12 && selectedYear === today.year()',
          @click='clickNextMonth',
        )
          svg(style='width:24px;height:24px' viewBox='0 0 24 24')
            path(fill='currentColor' d='M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z')


      .w-full
        template(v-if='showTimeSelector')
          .flex.flex-wrap.text-center.mt-1.mb-1(v-for='meridiem in meridiems')
            .text-xs.w-full.text-gray-600 {{meridiem}}
            .flex.w-1-6.justify-center(v-for='time in times')
              .flex.items-center.justify-center.h-2.w-2.my-_25.rounded-lg(
                :class='{ \
                  "cursor-pointer hover_bg-brand hover_text-white": !isTimeDisabled(time, meridiem), \
                  "bg-brand text-white": isTimeSelected(time, meridiem), \
                  "opacity-50 cursor-not-allowed": isTimeDisabled(time, meridiem), \
                }',
                @click='clickTime(time, meridiem, isTimeDisabled(time, meridiem))',
              ) {{time}}

        template(v-else)
          .flex
            .flex.my-_25.h-2.w-2.text-xs.text-gray-600.w-full.justify-center.items-center(
              v-for='(day, index) in days',
            ) {{day}}
          .flex(v-for='week in daysInMonthByWeek')
            .flex.my-_25.h-2.w-2.w-full.justify-center.items-center(
              v-for='(day, index) in week',
              :class='{ \
                "cursor-pointer hover_bg-brand hover_text-white": !isDayDisabled(day), \
                "bg-brand text-white": isDaySelected(day), \
                "bg-brand-100 text-brand-900 rounded-none": isDayHighlighted(day), \
                "opacity-50 cursor-not-allowed": isDayDisabled(day), \
                "rounded-lg": !isDayHighlighted(day) && !isFirstDay(day) && !isLastDay(day), \
                "rounded-l-lg": isRangePicker && isFirstDay(day), \
                "rounded-r-lg": isRangePicker && isLastDay(day), \
              }',
              @click='clickDate(day, isDayDisabled(day))',
              @mouseover='hoverDate(day)',
              @mouseleave='leaveDate(day)',
            ) {{day}}
</template>

<style>
@import '~import/index';

.vs-date {
  >input.vs-tab {
    &::placeholder {
      color: $textColor;
      @extend %letter-spacing-1;
    }

    &:hover::placeholder {
      color: white;
    }
  }

  .rounded-xl {
    border-radius: 1rem !important;
  }

  .rounded-lg {
    border-radius: .5rem !important;
  }

  .text-last-right {
    text-align-last: right;
  }

  .text-last-left {
    text-align-last: left;
  }

  .text-brand {
    color: #56bca6;
  }
}

.calendar {
  select {
    background: none;
  }
}
</style>